import React, { useState, useEffect } from "react";
import {
  PlusIcon,
  FolderIcon,
  RocketLaunchIcon,
  SparklesIcon,
} from "@heroicons/react/24/outline";
import { CreateProjectModal } from "./CreateProjectModal";
import { ShareProjectModal } from "../Shared/ShareProjectModal";
import { ProjectCard } from "./ProjectCard";
import { Button } from "../Shared/Button";
import { Card, CardContent } from "../Shared/Card";
import { supabase } from "../../lib/supabase";
import { useAuthStore } from "../../store/authStore";

interface Project {
  id: string;
  name: string;
  description: string;
  url: string;
  screenshot_url?: string;
  guest_password: string;
  created_at: string;
  member_count?: number;
}

export const ProjectsView = () => {
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isShareModalOpen, setIsShareModalOpen] = useState(false);
  const [selectedProject, setSelectedProject] = useState<Project | null>(null);
  const [projects, setProjects] = useState<Project[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const user = useAuthStore((state) => state.user);

  const fetchProjects = async () => {
    if (!user) return;

    setIsLoading(true);
    const { data, error } = await supabase
      .from("projects")
      .select("*")
      .eq("owner_id", user.id)
      .order("created_at", { ascending: false });

    if (error) {
      console.error("Error fetching projects:", error);
      setIsLoading(false);
      return;
    }

    setProjects(data || []);
    setIsLoading(false);
  };

  useEffect(() => {
    fetchProjects();
  }, [user]);

  const handleShare = (project: Project) => {
    setSelectedProject(project);
    setIsShareModalOpen(true);
  };

  // Loading skeleton
  const ProjectSkeleton = () => (
    <Card className="animate-pulse">
      <CardContent className="p-6">
        <div className="h-4 bg-gray-200 rounded w-3/4 mb-3" />
        <div className="h-3 bg-gray-200 rounded w-1/2 mb-4" />
        <div className="h-32 bg-gray-200 rounded mb-4" />
        <div className="flex gap-2">
          <div className="h-8 bg-gray-200 rounded w-20" />
          <div className="h-8 bg-gray-200 rounded w-16" />
        </div>
      </CardContent>
    </Card>
  );

  // Empty state
  const EmptyState = () => (
    <div className="col-span-full">
      <Card className="border-2 border-dashed border-gray-300 hover:border-primary-400 transition-colors">
        <CardContent className="p-12 text-center">
          <div className="mx-auto mb-6 h-24 w-24 rounded-full bg-gradient-to-br from-primary-100 to-secondary-100 flex items-center justify-center">
            <RocketLaunchIcon className="h-12 w-12 text-primary-600" />
          </div>

          <h3 className="text-xl font-semibold text-gray-900 mb-2">
            Ready to get feedback?
          </h3>

          <p className="text-gray-600 mb-8 max-w-md mx-auto">
            Create your first project to start collecting visual feedback from
            your team and clients.
          </p>

          <Button
            onClick={() => setIsCreateModalOpen(true)}
            size="lg"
            leftIcon={<PlusIcon className="h-5 w-5" />}
            className="shadow-large hover:shadow-glow"
          >
            Create Your First Project
          </Button>

          <div className="mt-8 flex items-center justify-center gap-8 text-sm text-gray-500">
            <div className="flex items-center gap-2">
              <div className="h-2 w-2 rounded-full bg-success-500" />
              <span>Easy setup</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="h-2 w-2 rounded-full bg-primary-500" />
              <span>Instant collaboration</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="h-2 w-2 rounded-full bg-secondary-500" />
              <span>Real-time feedback</span>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );

  return (
    <div className="space-y-8">
      {/* Action Header */}
      {projects.length > 0 && !isLoading && (
        <div className="flex justify-end">
          <Button
            onClick={() => setIsCreateModalOpen(true)}
            leftIcon={<PlusIcon className="h-5 w-5" />}
            className="shadow-medium hover:shadow-large"
          >
            New Project
          </Button>
        </div>
      )}

      {/* Projects Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 items-stretch">
        {isLoading ? (
          // Loading skeletons
          [...Array(6)].map((_, i) => <ProjectSkeleton key={i} />)
        ) : projects.length === 0 ? (
          // Empty state
          <EmptyState />
        ) : (
          // Projects
          projects.map((project, index) => (
            <div
              key={project.id}
              className="animate-fade-in-up"
              style={{ animationDelay: `${index * 0.1}s` }}
            >
              <ProjectCard
                project={project}
                onShare={() => handleShare(project)}
                onDeleted={fetchProjects}
                onUpdated={fetchProjects}
              />
            </div>
          ))
        )}
      </div>

      {/* Bottom CTA for existing projects */}
      {projects.length > 0 && !isLoading && (
        <Card className="border border-primary-200 bg-gradient-to-r from-primary-50 to-secondary-50">
          <CardContent className="p-8">
            <div className="flex items-center gap-6">
              <div className="flex-shrink-0">
                <div className="h-12 w-12 rounded-xl bg-gradient-to-br from-primary-500 to-secondary-500 flex items-center justify-center shadow-medium">
                  <SparklesIcon className="h-6 w-6 text-white" />
                </div>
              </div>

              <div className="flex-1">
                <h3 className="text-lg font-semibold text-gray-900 mb-1">
                  Need another project?
                </h3>
                <p className="text-gray-600">
                  Create additional projects to organize feedback for different
                  websites or teams.
                </p>
              </div>

              <Button
                onClick={() => setIsCreateModalOpen(true)}
                variant="outline"
                leftIcon={<PlusIcon className="h-4 w-4" />}
              >
                Add Project
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      <CreateProjectModal
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        onProjectCreated={fetchProjects}
      />

      {selectedProject && (
        <ShareProjectModal
          isOpen={isShareModalOpen}
          onClose={() => {
            setIsShareModalOpen(false);
            setSelectedProject(null);
          }}
          project={selectedProject}
        />
      )}
    </div>
  );
};
